import { useEffect, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import QRCode from "react-qr-code";
import { ArrowLeft, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { useAuth } from "../context/AuthContext";
import api from "../lib/api";
import { formatDateTime, getErrorMessage } from "../lib/formatters";


const formatCountdown = (seconds) => {
  const safeSeconds = Math.max(0, Number(seconds || 0));
  const minutes = String(Math.floor(safeSeconds / 60)).padStart(2, "0");
  const remainingSeconds = String(safeSeconds % 60).padStart(2, "0");
  return `${minutes}:${remainingSeconds}`;
};


export default function KioskLivePage() {
  const { sessionId } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  const [session, setSession] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [refreshing, setRefreshing] = useState(false);
  const viewerToken = searchParams.get("viewer_token");
  const isPublicViewer = Boolean(viewerToken);

  const fetchSession = async (showSpinner = false) => {
    if (showSpinner) setRefreshing(true);
    try {
      const response = isPublicViewer
        ? await api.get(`/public/kiosk/sessions/${sessionId}/state`, { params: { viewer_token: viewerToken } })
        : await api.get(`/kiosk/sessions/${sessionId}/state`);
      setSession(response.data);
      setError("");
      return true;
    } catch (requestError) {
      setError(getErrorMessage(requestError));
      return false;
    } finally {
      setLoading(false);
      if (showSpinner) setRefreshing(false);
    }
  };

  useEffect(() => {
    fetchSession();
    const pollInterval = window.setInterval(() => {
      fetchSession();
    }, 5000);
    return () => window.clearInterval(pollInterval);
  }, [isPublicViewer, sessionId, viewerToken]);

  useEffect(() => {
    if (!session || session.expired) return undefined;
    const countdownInterval = window.setInterval(() => {
      setSession((current) => {
        if (!current) return current;
        return { ...current, seconds_remaining: Math.max(0, (current.seconds_remaining || 0) - 1) };
      });
    }, 1000);
    return () => window.clearInterval(countdownInterval);
  }, [session?.expired]);

  const handleManualRefresh = async () => {
    const success = await fetchSession(true);
    if (success) {
      toast.success("Kiosk session refreshed");
    }
  };

  return (
    <div className="min-h-screen bg-[#FAF8F3] px-4 py-6 sm:px-6 lg:px-8" data-testid="kiosk-live-page-root">
      <div className="mx-auto grid min-h-[calc(100vh-3rem)] max-w-[1400px] gap-6 xl:grid-cols-[0.72fr_0.28fr]">
        <Card className="border-slate-200/70 bg-white/90 shadow-[0_30px_90px_rgba(15,23,42,0.08)]" data-testid="kiosk-live-qr-card">
          <CardHeader className="space-y-4">
            <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
              <div>
                <p className="text-xs font-semibold uppercase tracking-[0.28em] text-[#C27D00]" data-testid="kiosk-live-caption">Tarka HR kiosk mode</p>
                <CardTitle className="text-4xl font-extrabold" data-testid="kiosk-live-title">Dynamic attendance QR</CardTitle>
                <CardDescription data-testid="kiosk-live-description">Keep this window open on the kiosk screen. The QR auto-rotates every minute for short-lived attendance scans.</CardDescription>
                {isPublicViewer && !user ? <p className="mt-3 text-sm text-slate-500" data-testid="kiosk-live-public-copy">This public kiosk display does not require an admin login.</p> : null}
              </div>
              <div className="flex flex-wrap gap-2" data-testid="kiosk-live-header-actions">
                <Button type="button" variant="outline" className="rounded-2xl" onClick={() => (isPublicViewer ? window.close() : navigate("/company/kiosk"))} data-testid="kiosk-live-back-button">
                  <ArrowLeft className="mr-2 h-4 w-4" />
                  {isPublicViewer ? "Close window" : "Back to kiosk"}
                </Button>
                <Button type="button" className="rounded-2xl bg-[#F4A300] text-[#0D1B2A] hover:bg-[#D89000]" onClick={handleManualRefresh} disabled={refreshing} data-testid="kiosk-live-refresh-button">
                  <RefreshCw className="mr-2 h-4 w-4" />
                  {refreshing ? "Refreshing..." : "Refresh now"}
                </Button>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="rounded-[32px] border border-slate-200 bg-slate-50 p-16 text-center text-slate-500" data-testid="kiosk-live-loading-state">
                Loading live kiosk session...
              </div>
            ) : null}

            {!loading && error ? (
              <div className="rounded-[32px] border border-rose-200 bg-rose-50 p-12 text-center" data-testid="kiosk-live-error-state">
                <p className="text-lg font-bold text-rose-700" data-testid="kiosk-live-error-message">{error}</p>
              </div>
            ) : null}

            {!loading && !error && session ? (
              <div className="grid gap-6 lg:grid-cols-[0.68fr_0.32fr]" data-testid="kiosk-live-content-grid">
                <div className="rounded-[32px] border border-slate-200 bg-slate-50 p-6" data-testid="kiosk-live-qr-panel">
                  {session.scan_url ? (
                    <div className="mx-auto aspect-square max-w-[680px] rounded-[28px] bg-white p-6 shadow-sm" data-testid="kiosk-live-qr-wrap">
                      <QRCode value={session.scan_url} className="h-full w-full" />
                    </div>
                  ) : (
                    <div className="mx-auto flex aspect-square max-w-[680px] items-center justify-center rounded-[28px] border border-dashed border-slate-200 bg-white p-6 text-center text-sm text-slate-400" data-testid="kiosk-live-no-qr-state">
                      This kiosk session is no longer live.
                    </div>
                  )}
                </div>
                <div className="grid gap-4" data-testid="kiosk-live-metrics-grid">
                  <div className="rounded-[28px] border border-slate-200 bg-white p-6" data-testid="kiosk-live-session-metrics-card">
                    <p className="text-xs font-semibold uppercase tracking-[0.26em] text-slate-400" data-testid="kiosk-live-session-label">Session label</p>
                    <p className="mt-2 text-2xl font-extrabold text-slate-900" data-testid="kiosk-live-session-value">{session.label}</p>
                    <div className="mt-6 grid gap-4 sm:grid-cols-2 xl:grid-cols-1">
                      <div>
                        <p className="text-xs font-semibold uppercase tracking-[0.22em] text-slate-400" data-testid="kiosk-live-next-rotation-label">Next rotation</p>
                        <p className="mt-2 text-4xl font-extrabold text-slate-900" data-testid="kiosk-live-next-rotation-value">{formatCountdown(session.seconds_remaining)}</p>
                      </div>
                      <div>
                        <p className="text-xs font-semibold uppercase tracking-[0.22em] text-slate-400" data-testid="kiosk-live-session-expiry-label">Session ends</p>
                        <p className="mt-2 text-lg font-semibold text-slate-900" data-testid="kiosk-live-session-expiry-value">{formatDateTime(session.session_ends_at)}</p>
                      </div>
                    </div>
                  </div>

                  <div className="rounded-[28px] border border-slate-200 bg-white p-6" data-testid="kiosk-live-status-card">
                    <p className="text-xs font-semibold uppercase tracking-[0.22em] text-slate-400" data-testid="kiosk-live-status-label">Status</p>
                    <p className="mt-2 text-2xl font-extrabold text-slate-900" data-testid="kiosk-live-status-value">{session.expired ? "Expired" : "Live and rotating"}</p>
                    <p className="mt-3 text-sm text-slate-500" data-testid="kiosk-live-window-count-copy">Opened in new windows: <span className="font-semibold text-slate-900" data-testid="kiosk-live-window-count-value">{session.opened_window_count || 0}</span></p>
                    <p className="mt-4 break-all text-xs text-slate-500" data-testid="kiosk-live-scan-url">{session.scan_url}</p>
                  </div>
                </div>
              </div>
            ) : null}
          </CardContent>
        </Card>

        <div className="grid gap-6" data-testid="kiosk-live-side-panel">
          <Card className="border-[#0D1B2A]/10 bg-[#0D1B2A] text-white" data-testid="kiosk-live-instructions-card">
            <CardHeader>
              <CardTitle className="text-2xl font-extrabold" data-testid="kiosk-live-instructions-title">Display tips</CardTitle>
              <CardDescription className="text-slate-300" data-testid="kiosk-live-instructions-description">Use this page in a dedicated kiosk browser window near your office entrance.</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4 text-sm text-slate-300">
              <div data-testid="kiosk-live-instruction-1">1. Keep the browser full-screen on the kiosk monitor.</div>
              <div data-testid="kiosk-live-instruction-2">2. Employees scan only the currently visible QR.</div>
              <div data-testid="kiosk-live-instruction-3">3. The code refreshes itself every minute, so no manual action is needed.</div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}